import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function EditarActividad() {
    const { sistemaId, tareaId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();

    const [formData, setFormData] = useState({
        titulo: '',
        descripcion: '',
        fecha_vencimiento: '',
        agencia_id: '', // La tarea solo tiene una agencia
        trabajo_id: '',
        usuariosAsignados: [],
        subtareas: [{ id: null, titulo: '' }],
        es_recurrente: false,
        recurrencia_frecuencia: 'semanal',
        recurrencia_intervalo: 1,
        recurrencia_dias_semana: [],
        recurrencia_fecha_final: ''
    });

    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaAgencias, setListaAgencias] = useState([]);
    const [listaTrabajos, setListaTrabajos] = useState([]);
    const [incluirSubtareas, setIncluirSubtareas] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const cargarDatos = async () => {
            if (!token) return;
            setLoading(true);
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                const [tareaRes, usuariosRes, agenciasRes, trabajosRes] = await Promise.all([
                    fetch(`/api/actsis/tareas/${tareaId}`, { headers }),
                    fetch(`/api/sistemas/${sistemaId}/usuarios`, { headers }),
                    fetch('/api/agencias-accesibles', { headers }), // <-- LÍNEA CORREGIDA
                    fetch('/api/trabajos', { headers })
                ]);
                
                if (!tareaRes.ok || !usuariosRes.ok || !agenciasRes.ok || !trabajosRes.ok) {
                    // Intenta obtener un mensaje de error específico si es posible
                    const errorData = await tareaRes.json().catch(() => ({ message: 'Error al cargar los datos necesarios para la edición.' }));
                    throw new Error(errorData.message);
                }

                const tareaData = await tareaRes.json();
                const usuariosData = await usuariosRes.json();
                const agenciasData = await agenciasRes.json();
                const trabajosData = await trabajosRes.json();

                setListaUsuarios(usuariosData);
                setListaAgencias(agenciasData);
                setListaTrabajos(trabajosData);

                const formattedDate = tareaData.fecha_vencimiento ? new Date(tareaData.fecha_vencimiento).toISOString().split('T')[0] : '';
                const formattedEndDate = tareaData.recurrencia_fecha_final ? new Date(tareaData.recurrencia_fecha_final).toISOString().split('T')[0] : '';

                // --- LÓGICA DE CARGA DE DATOS CORREGIDA ---
                setFormData({
                    titulo: tareaData.titulo || '',
                    descripcion: tareaData.descripcion || '',
                    fecha_vencimiento: formattedDate,
                    agencia_id: tareaData.agencia_id || '',
                    trabajo_id: tareaData.trabajo_id || '',
                    usuariosAsignados: tareaData.usuarios_asignados || [],
                    subtareas: tareaData.subtareas && tareaData.subtareas.length > 0 ? tareaData.subtareas : [{ id: null, titulo: '' }],
                    es_recurrente: tareaData.es_recurrente || false,
                    recurrencia_frecuencia: tareaData.recurrencia_frecuencia || 'semanal',
                    recurrencia_intervalo: tareaData.recurrencia_intervalo || 1,
                    recurrencia_dias_semana: tareaData.recurrencia_dias_semana || [],
                    recurrencia_fecha_final: formattedEndDate
                });
                setIncluirSubtareas(tareaData.subtareas && tareaData.subtareas.length > 0);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        cargarDatos();
    }, [token, sistemaId, tareaId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubtareaChange = (index, value) => {
        const nuevasSubtareas = JSON.parse(JSON.stringify(formData.subtareas));
        nuevasSubtareas[index].titulo = value;
        setFormData(prev => ({...prev, subtareas: nuevasSubtareas}));
    };
    const agregarSubtarea = () => setFormData(prev => ({ ...prev, subtareas: [...prev.subtareas, { id: null, titulo: '' }] }));
    const quitarSubtarea = (index) => setFormData(prev => ({ ...prev, subtareas: prev.subtareas.filter((_, i) => i !== index) }));
    const handleUserAssign = (userId) => {
        setFormData(prev => ({...prev, usuariosAsignados: prev.usuariosAsignados.includes(userId) ? prev.usuariosAsignados.filter(id => id !== userId) : [...prev.usuariosAsignados, userId]}));
    };

    const handleDiaSemanaChange = (dia) => {
        setFormData(prev => ({...prev, recurrencia_dias_semana: prev.recurrencia_dias_semana.includes(dia) ? prev.recurrencia_dias_semana.filter(d => d !== dia) : [...prev.recurrencia_dias_semana, dia] }));
    };
    
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        const body = {
            titulo: formData.titulo,
            descripcion: formData.descripcion,
            fecha_vencimiento: formData.fecha_vencimiento,
            trabajo_id: formData.trabajo_id,
            agencia_id: formData.agencia_id,
            usuarios_asignados: formData.usuariosAsignados, // Nombre de campo corregido para el backend
            subtareas: incluirSubtareas ? formData.subtareas.filter(st => st.titulo && st.titulo.trim() !== '') : [],
            es_recurrente: formData.es_recurrente,
            recurrencia_frecuencia: formData.es_recurrente ? formData.recurrencia_frecuencia : null,
            recurrencia_intervalo: formData.es_recurrente ? formData.recurrencia_intervalo : null,
            recurrencia_dias_semana: formData.es_recurrente ? formData.recurrencia_dias_semana : [],
            recurrencia_fecha_final: formData.es_recurrente ? formData.recurrencia_fecha_final || null : null,
        };

        try {
            const response = await fetch(`/api/actsis/tareas/${tareaId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error al actualizar la tarea.');
            
            navigate(`/sistemas/${sistemaId}`);
        } catch (err) {
            setError(err.message);
        }
    };
    
    if (loading) return <div className="text-center mt-5"><div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div></div>;

    
    return (
        <div className="container mt-4 mb-5">
             <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2>Editar Actividad</h2>
                        <Link to={`/sistemas/${sistemaId}`} className="btn btn-secondary">Cancelar</Link>
                    </div>

                    {error && <div className="alert alert-danger">{error}</div>}
                    
                    <form onSubmit={handleSubmit} className="card shadow-sm p-4">
                        <div className="mb-3">
                            <label htmlFor="titulo" className="form-label">Título</label>
                            <input type="text" id="titulo" name="titulo" className="form-control" value={formData.titulo} onChange={handleChange} required />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="descripcion" className="form-label">Descripción</label>
                            <textarea id="descripcion" name="descripcion" className="form-control" rows="3" value={formData.descripcion} onChange={handleChange}></textarea>
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label htmlFor="trabajo" className="form-label">Tipo de Trabajo</label>
                                <select id="trabajo" name="trabajo_id" className="form-select" value={formData.trabajo_id} onChange={handleChange} required>
                                    <option value="">Selecciona un trabajo...</option>
                                    {listaTrabajos.map(trabajo => <option key={trabajo.id} value={trabajo.id}>{trabajo.nombre}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label htmlFor="fechaVencimiento" className="form-label">Vencimiento</label>
                                <input type="date" id="fechaVencimiento" name="fecha_vencimiento" className="form-control" value={formData.fecha_vencimiento} onChange={handleChange} required />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="agencia" className="form-label">Agencia</label>
                            <select id="agencia" name="agencia_id" className="form-select" value={formData.agencia_id} onChange={handleChange} required>
                                <option value="">Selecciona una agencia...</option>
                                {listaAgencias.map(agencia => <option key={agencia.id} value={agencia.id}>{agencia.nombre}</option>)}
                            </select>
                        </div>
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="incluirSubtareas" checked={incluirSubtareas} onChange={(e) => setIncluirSubtareas(e.target.checked)} />
                            <label className="form-check-label" htmlFor="incluirSubtareas">Editar Subtareas</label>
                        </div>
                        {incluirSubtareas && (
                            <div className="mb-3 ps-4">
                                {formData.subtareas.map((subtarea, index) => (
                                    <div className="input-group mb-2" key={subtarea.id || index}>
                                        <input type="text" className="form-control" placeholder={`Subtarea ${index + 1}`} value={subtarea.titulo} onChange={(e) => handleSubtareaChange(index, e.target.value)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => quitarSubtarea(index)}>–</button>
                                    </div>
                                ))}
                                <button className="btn btn-outline-primary btn-sm" type="button" onClick={agregarSubtarea}>+ Agregar Subtarea</button>
                            </div>
                        )}
                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" role="switch" id="esRecurrente" name="es_recurrente" checked={formData.es_recurrente} onChange={handleChange} />
                            <label className="form-check-label" htmlFor="esRecurrente">Tarea Recurrente</label>
                        </div>
                        {formData.es_recurrente && (
                            <div className="border p-3 rounded mb-3">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="frecuencia" className="form-label">Frecuencia</label>
                                        <select id="frecuencia" name="recurrencia_frecuencia" className="form-select" value={formData.recurrencia_frecuencia} onChange={handleChange}>
                                            <option value="diaria">Diaria</option>
                                            <option value="semanal">Semanal</option>
                                            <option value="mensual">Mensual</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="intervalo" className="form-label">Cada</label>
                                        <div className="input-group">
                                            <input type="number" id="intervalo" name="recurrencia_intervalo" className="form-control" value={formData.recurrencia_intervalo} onChange={handleChange} min="1" />
                                            <span className="input-group-text">{formData.recurrencia_frecuencia === 'semanal' ? 'semana(s)' : formData.recurrencia_frecuencia === 'mensual' ? 'mes(es)' : 'día(s)'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="fechaFinal" className="form-label">Finaliza (Opcional)</label>
                                        <input type="date" id="fechaFinal" name="recurrencia_fecha_final" className="form-control" value={formData.recurrencia_fecha_final} onChange={handleChange} />
                                    </div>
                                </div>
                                {formData.recurrencia_frecuencia === 'semanal' && (
                                    <div className="mt-3">
                                        <label className="form-label">Repetir los días:</label>
                                        <div>
                                            {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((dia, index) => (
                                                <div className="form-check form-check-inline" key={index}>
                                                    <input className="form-check-input" type="checkbox" id={`dia-${index}`} checked={formData.recurrencia_dias_semana.includes(index + 1)} onChange={() => handleDiaSemanaChange(index + 1)} />
                                                    <label className="form-check-label" htmlFor={`dia-${index}`}>{dia}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label">Asignar a:</label>
                            <div className="border rounded p-2" style={{maxHeight: '150px', overflowY: 'auto'}}>
                                {listaUsuarios.map(user => (
                                    <div className="form-check" key={user.id}>
                                        <input className="form-check-input" type="checkbox" value={user.id} id={`user-${user.id}`} checked={formData.usuariosAsignados.includes(user.id)} onChange={() => handleUserAssign(user.id)} />
                                        <label className="form-check-label" htmlFor={`user-${user.id}`}>{user.nombre}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="text-end">
                            <button type="submit" className="btn btn-primary">Guardar Cambios</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditarActividad;